import React, { ChangeEvent, FC, ReactElement, useEffect, useState } from 'react';
import './FormFields.sass';
import InputMask from 'react-input-mask';
import { Preloader } from 'src/components/Elements/Preloader/Preloader';
import { useSelector } from 'react-redux';
import { getAllStyle } from '../../../stores/reducers/sections';
import { Field, FieldOption, PropsObj, RegistrationField } from './types';
import alwaysRequiredFields from '../../../json/alwaysRequiredFields';
import localeFieldResolver from '../../../helpers/locale-field-finder';
import { internalizationState } from '../../../stores/reducers/sections';
import cns from 'classnames';
import { values } from 'lodash';
export interface Props {
  registrationFields: RegistrationField[] | null;
  // guestInfo: any;
  handleFieldChange: (
    e: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLSelectElement>,
    name: string | undefined
  ) => void;
  inputValues: any;
  participationType?: string;
}

const FormFields: FC<Props> = ({
    registrationFields,
    inputValues,
    participationType = '', 
    handleFieldChange,
}): ReactElement => {
  const [pairedFields, setPairedFields]: any = useState([]);
  const styles = useSelector(getAllStyle);
  const { activeLangState } = useSelector(internalizationState);

  useEffect(() => {
    if (registrationFields) {
      const formattedRegistrationFields = registrationFields?.map((f: any) => ({
        ...f,
        field: {
          ...f.field,
          id: f.field.name,
        },
      }));

      const orderedFields =
        registrationFields &&
        [...(formattedRegistrationFields as any), ...alwaysRequiredFields]
          .sort((a, b) => a.order - b.order)
          .map((field) => ({ ...field.field, required: field.required }));

      const uniqFields = orderedFields?.filter(
        (field, index, self) => index === self.findIndex((t) => t.id === field.id)
      );

      const tempArr = [];
      if (uniqFields) {
        for (let i = 0; i < uniqFields.length; i += 2) {
          tempArr.push(uniqFields.slice(i, i + 2));
        }
      }
      setPairedFields(tempArr);
    }
  }, [registrationFields]);

  const renderInput = (field: Field) => {
    const propsObj: PropsObj = {};

    if (field.id) propsObj.id = field.id.toString();
    if (field.name) propsObj.name = field.name;
    if (field.placeholder_ru || field.placeholder_en) propsObj.placeholder = localeFieldResolver(field, 'placeholder_ru', activeLangState);

    if (field.required || ['surname', 'name', 'email'].includes(field.name)) {
      propsObj.required = true;
    }

    if (field.name !== 'phone' && field.name !== 'TEL') {
      if (field.max) propsObj.maxLength = field.max;
      if (field.min) propsObj.minLength = field.min;
      if (field.regexp) propsObj.pattern = field.regexp;
    }

    let value = '';
    // if (guestInfo && Object.keys(guestInfo).includes(field.name)) {
    //   value = guestInfo[field.name];
    // }

    switch (field.name) {
      case 'email':
        return (
          <input
            {...propsObj}
            defaultValue={value}
            disabled={!!value}
            onChange={(e) => handleFieldChange(e, propsObj.name)}
            type="email"
          />
        );
      case 'city':
        return (
          <input
            {...propsObj}
            defaultValue={value}
            disabled={!!value}
            onChange={(e) => handleFieldChange(e, propsObj.name)}
            type="text"
          />
        );
      case 'phone':
      case 'TEL':
        return (
          <InputMask
            {...propsObj}
            defaultValue={value}
            disabled={!!value}
            onChange={(e) => handleFieldChange(e, propsObj.name)}
            mask="+7 (999) 999-99-99"
          />
        );
      default:
        return (
          <input
            {...propsObj}
            defaultValue={value}
            disabled={!!value}
            onChange={(e) => handleFieldChange(e, propsObj.name)}
            type={field.type === 'text' ? 'text' : field.type}
          />
        );
    }
  };

  const renderOption = (field: Field) => {
    const isParticipationTypeHybrid = participationType === '' || participationType === 'hybrid';

    return (
      <select
        id={field.id.toString()}
        value={inputValues[field.name] || 0}
        required={field.required}
        disabled={field.name === 'participationType' && !isParticipationTypeHybrid}
        style={!inputValues[field.name] || inputValues[field.name] === '0' ? { color: 'rgba(31, 32, 65, 0.38)' } : {}}
        onChange={(e) => handleFieldChange(e, field.name)}
      >
        <option value="">
          {localeFieldResolver(field, 'placeholder_ru', activeLangState)}
        </option>
        {field.options.map((option: FieldOption, ind2: number) => {
          return (
            <option key={ind2} id={option.id.toString()} value={option.value}>
              { localeFieldResolver(option, 'label_ru', activeLangState) }
            </option>
          );
        })}
      </select>
    )
  };

  const renderCheckbox = (field: Field) => {
    const propsObj: PropsObj = {};

    if (field.id) propsObj.id = field.id.toString();
    if (field.name) propsObj.name = field.name;
    if (field.required) propsObj.required = field.required;

    return (
      <div className="wrapper-agreement registration-checkbox-wrapper">
        <label>
          <span>
            {localeFieldResolver(field, 'label_ru', activeLangState)}
            {field.required && <span style={{ color: 'red' }}>*</span>}
          </span>
          <input
            {...propsObj}
            type="checkbox"
            className="registration-checkbox"
            defaultChecked={false}
            onChange={(e) => handleFieldChange(e, propsObj.name)}
          />
          <span className="checkmark"></span>
        </label>
      </div>
    );
  }

  const renderFormFields = (field: Field) => {
    if (field.type === 'text' || field.type === 'number') {
      return renderInput(field)
    } else if (field.type === 'checkbox') {
      return renderCheckbox(field);
    } else {
      return renderOption(field)
    }
  };

  return pairedFields.length ? (
    pairedFields.map((twoFields: any, i: number) => (
      <div
        key={i}
        className={
          cns(
            pairedFields.flat().length % 2 !== 0 ? 'column' : 'row',
          )
        }
      >
        {twoFields.map((field: Field, ind: number) => (
          <div key={ind} className="wrapper-input">
            {
              field.type !== 'checkbox' &&
              <label htmlFor={field.id.toString()}>
                {localeFieldResolver(field, 'label_ru', activeLangState)}
                {field.required && <span style={{ color: 'red' }}>*</span>}
              </label>
            }
            {
              renderFormFields(field)
            }
          </div>
        ))}
      </div>
    ))
  ) : (
    <Preloader styles={styles} />
  );
};

export default FormFields;
